import React, { useEffect, useRef, useState } from "react";

interface Props {
  src: string;
  alt?: string;
  zoom?: number;
  lensSize?: number;
  className?: string;
  style?: React.CSSProperties;
  onTap?: () => void;
}

interface LensState {
  left: number;
  top: number;
  bgX: number;
  bgY: number;
  bgW: number;
  bgH: number;
}

// Amazon-style hover loupe. Wraps the artwork image; hovering shows a
// follower lens that displays the source pixels at `zoom×`. Self-disables
// on touch devices via the `(hover: none) or (pointer: coarse)` media
// query so the lens never gets stuck visible after a tap — touch users
// get the `onTap` callback (parent typically opens a fullscreen lightbox).
//
// Rendered as a `background-image` div (not `<img>`) to match the existing
// slider markup; boneyard's snapshot extractor applies a 50% border-radius
// heuristic to square `<img>` tags which would otherwise circle the
// skeleton.
const HoverMagnifier: React.FC<Props> = ({
  src,
  alt,
  zoom = 2.5,
  lensSize = 180,
  className,
  style,
  onTap,
}) => {
  const wrapperRef = useRef<HTMLDivElement>(null);
  const [natural, setNatural] = useState<{ w: number; h: number } | null>(null);
  const [lens, setLens] = useState<LensState | null>(null);

  // Preload to capture naturalWidth/Height — needed for the contain-fit
  // math that maps cursor coordinates to image coordinates.
  useEffect(() => {
    if (!src) return;
    setNatural(null);
    setLens(null);
    const img = new Image();
    img.onload = () =>
      setNatural({ w: img.naturalWidth, h: img.naturalHeight });
    img.src = src;
  }, [src]);

  const handleMouseMove = (e: React.MouseEvent<HTMLDivElement>) => {
    if (!natural || !wrapperRef.current) return;
    const rect = wrapperRef.current.getBoundingClientRect();
    if (rect.width === 0 || rect.height === 0) return;
    const cx = e.clientX - rect.left;
    const cy = e.clientY - rect.top;

    // Rendered image rect inside the wrapper, assuming
    // background-size: contain.
    const containerRatio = rect.width / rect.height;
    const imageRatio = natural.w / natural.h;
    let renderedW: number;
    let renderedH: number;
    let offsetX: number;
    let offsetY: number;
    if (imageRatio > containerRatio) {
      renderedW = rect.width;
      renderedH = rect.width / imageRatio;
      offsetX = 0;
      offsetY = (rect.height - renderedH) / 2;
    } else {
      renderedH = rect.height;
      renderedW = rect.height * imageRatio;
      offsetX = (rect.width - renderedW) / 2;
      offsetY = 0;
    }

    // Cursor in rendered-image coordinates (origin = top-left of image).
    const ix = cx - offsetX;
    const iy = cy - offsetY;
    if (ix < 0 || iy < 0 || ix > renderedW || iy > renderedH) {
      setLens(null);
      return;
    }

    // Lens screen position (relative to wrapper), clamped so the lens
    // can't extend past the wrapper bounds.
    const left = Math.max(
      0,
      Math.min(cx - lensSize / 2, rect.width - lensSize)
    );
    const top = Math.max(
      0,
      Math.min(cy - lensSize / 2, rect.height - lensSize)
    );

    // Magnified image is sized so the visible image area becomes
    // renderedW * zoom × renderedH * zoom. The point under the cursor
    // (ix * zoom, iy * zoom) is centered in the lens.
    const bgW = renderedW * zoom;
    const bgH = renderedH * zoom;
    const bgX = -(ix * zoom - lensSize / 2);
    const bgY = -(iy * zoom - lensSize / 2);

    setLens({ left, top, bgX, bgY, bgW, bgH });
  };

  const handleMouseLeave = () => setLens(null);

  const handleClick = () => {
    if (onTap) onTap();
  };

  return (
    <div
      ref={wrapperRef}
      className={`hoverMagnifier${className ? ` ${className}` : ""}`}
      style={style}
      onMouseMove={handleMouseMove}
      onMouseLeave={handleMouseLeave}
      onClick={handleClick}
    >
      <div
        role="img"
        aria-label={alt}
        className="hoverMagnifier__bg"
        style={{
          backgroundColor: "#eee",
          backgroundImage: `url(${src})`,
          backgroundSize: "contain",
          backgroundRepeat: "no-repeat",
          backgroundPosition: "center center",
          width: "100%",
          height: "100%",
          borderRadius: "inherit",
        }}
      />
      {lens && (
        <div
          className="hoverMagnifier__lens"
          style={{
            left: lens.left,
            top: lens.top,
            width: lensSize,
            height: lensSize,
            backgroundImage: `url(${src})`,
            backgroundSize: `${lens.bgW}px ${lens.bgH}px`,
            backgroundPosition: `${lens.bgX}px ${lens.bgY}px`,
            backgroundRepeat: "no-repeat",
          }}
        />
      )}
    </div>
  );
};

export default HoverMagnifier;
